import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import MuteIcon from '@components/icons/MuteIcon/MuteIcon'
import LineClampCollapse from '@components/ui/LineClampCollapse/LineClampCollapse'
import { isMobile } from '@utils/helpers/DeviceUtils'
import drawImageProp from '@utils/helpers/drawImageProp'
import { cover } from '@utils/helpers/intrinsicScale'
import clsx from 'clsx'
import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { Howl } from 'howler'
import { throttle } from 'lodash-es'
import { FC, useEffect, useRef, useState } from 'react'
import { MediaPicker } from 'types/mediaPicker'
import s from './ScrollVideo.module.scss'

// ffmpeg -i input.mp4 -movflags faststart -vcodec libx264 -crf 23 -g 1 -pix_fmt yuv420p output.mp4
// ffmpeg -i input.mp4 -vf scale=960:-1 -movflags faststart -vcodec libx264 -crf 20 -g 1 -pix_fmt yuv420p output_960.mp4
type Notification = {
  startingFrame?: number
  endingFrame?: number
  mobileStartingFrame?: number
  mobileEndingFrame?: number
  title?: string
  text?: string
  enableNewsletter: boolean
  labelEmailPlaceHolder?: string
  labelProfessional?: string
  labelPrivate?: string
  labelAcceptTerms?: string
  labelSignUpButton?: string
  alignLeft?: boolean
}
type ScrollVideoProps = {
  videoID: string
  mobileVideoID: string
  frameCount: number
  mobileFrameCount?: number
  mobileScrollHeight?: number
  audio?: MediaPicker
  automaticScroll: boolean
  autoScrollTimer: number
  scrollHeight: number
  notifications: Notification[]
}

const ScrollVideo: FC<ScrollVideoProps> = ({
  audio,
  automaticScroll,
  frameCount,
  mobileFrameCount,
  mobileScrollHeight,
  scrollHeight,
  notifications,
  videoID,
  autoScrollTimer,
  mobileVideoID,
}) => {
  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)
  const [sound, setSound] = useState<Howl>()
  const [scrolling, setScrolling] = useState<boolean>(false)
  const [autoScroll, setAutoScroll] = useState<boolean>(automaticScroll)
  const [muted, setMuted] = useState<boolean>(true)
  const canvas = useRef<HTMLCanvasElement>(null)
  const canvasPin = useRef<HTMLDivElement>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  const componentContainer = useRef<HTMLDivElement>(null)
  const timer = useRef<any>(null)
  const section = useRef<HTMLElement>(null)
  const audioElement = useRef<HTMLAudioElement>(null)
  const debounce = (fn: Function, ms = 300) => {
    return function (this: any, ...args: any[]) {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => fn.apply(this, args), ms)
    }
  }

  const allowAutoScroll = useRef<boolean>(true)
  const lastScrollTop = useRef<number>(0)

  useEffect(() => {
    if (!autoScroll) return
    const handleAutoScroll = throttle((e) => {
      let currentScroll =
        document.documentElement.scrollTop || document.body.scrollTop // Get Current Scroll Value
      if (currentScroll > 0 && lastScrollTop.current <= currentScroll) {
        lastScrollTop.current = currentScroll
        allowAutoScroll.current = true
      } else {
        lastScrollTop.current = currentScroll
        allowAutoScroll.current = false
      }
    }, 100)

    const handleScroll = throttle((e) => {
      debounce(
        () => {
          setScrolling(true)
        },
        autoScrollTimer === 0 ? 3000 : autoScrollTimer,
      )()

      setScrolling(false)
    }, 200)

    document.addEventListener('scroll', handleAutoScroll)
    document.addEventListener('wheel', handleScroll)
    document.addEventListener('touchmove', handleScroll)

    return () => {
      document.removeEventListener('wheel', handleScroll)
      document.removeEventListener('scroll', handleAutoScroll)
      document.removeEventListener('touchmove', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (!autoScroll) return
    const x = setInterval(() => {
      requestAnimationFrame(() => {
        if (
          scrolling &&
          ScrollTrigger.isInViewport(section.current!) &&
          autoScroll
        ) {
          if (!allowAutoScroll.current) {
            setScrolling(false)
            return
          }

          if (isMobile()) {
            window.scrollBy(0, 4)
          } else {
            window.scrollBy(0, 2)
          }
        } else {
          clearInterval(x)
        }
      })
    }, 1)
    return () => {
      clearInterval(x)
    }
  }, [scrolling, autoScroll])

  const currentFrame = (index: number): string => {
    return `https://${process.env
      .NEXT_PUBLIC_CLOUDFRONT_DISTRO!}.cloudfront.net/${
      isMobile() ? mobileVideoID : videoID
    }/${index.toString()}.webp`
  }

  useEffect(() => {
    if (!canvas.current || !section.current || !componentContainer.current)
      return

    const context = canvas.current.getContext('2d', { alpha: false })

    const images: HTMLImageElement[] = []
    const frames = {
      frame: 0,
    }
    const fCount = isMobile() ? (mobileFrameCount ?? frameCount) : frameCount
    const sHeight = isMobile()
      ? (mobileScrollHeight ?? scrollHeight)
      : scrollHeight

    for (let i = 0; i < fCount; i++) {
      const img = new Image()
      img.src = currentFrame(i)
      images.push(img)
    }

    gsap.to(frames, {
      frame: fCount - 1,
      snap: 'frame',
      ease: 'none',
      scrollTrigger: {
        scrub: 0.5,
        pin: canvasPin.current,
        end: () => `+=${sHeight}px`,
        onEnter: () => {
          if (!autoScroll) return
          debounce(() => {
            setScrolling(true)
          }, 5000)()
        },
      },
      onUpdate: render, // use animation onUpdate instead of scrollTrigger's onUpdate
    })

    gsap.utils
      .toArray(componentContainer.current.children)
      .forEach((target: any) => {
        const targetTl = gsap
          .timeline({ paused: true })
          .fromTo(
            target,
            { autoAlpha: 0, display: 'none' },
            { autoAlpha: 1, display: 'flex', duration: 0.5 },
          )
        const compTl = gsap.timeline({
          scrollTrigger: {
            trigger: section.current,
            start: () =>
              `${(target.getAttribute('data-start') / fCount) * sHeight}px top`,
            end: () =>
              `${(target.getAttribute('data-end') / fCount) * sHeight}px`,
            onEnter: () => {
              targetTl.play()
            },
            onEnterBack: () => {
              targetTl.play()
            },
            onLeaveBack: () => {
              targetTl.reverse()
            },
            onLeave: () => {
              targetTl.reverse()
            },
          },
        })
      })

    images[0].onload = () => {
      canvas.current!.width = images[0].naturalWidth
      canvas.current!.height = images[0].naturalHeight

      render()
    }

    function render() {
      if (!context || !canvas.current) return

      const img = images[frames.frame]

      const { offsetX, offsetY, width, height } = cover(
        canvas.current.width,
        canvas.current.height,
        img.width,
        img.height,
      )

      context.clearRect(0, 0, width, height)
      drawImageProp(
        context,
        img,
        0,
        0,
        canvas.current.width,
        canvas.current.height,
      )

      // var t = Math.floor((canvas.current.width - img.naturalWidth) / 2),
      //   r = Math.floor((canvas.current.height - img.naturalHeight) / 2)
      // context.drawImage(img, t, r)
    }
  }, [])
  useEffect(() => {
    if (!audioElement.current) return
    audioElement.current.play()
  }, [audioElement.current])

  useEffect(() => {
    if (audio?.url) {
      const sound = new Howl({
        src: audio?.url!,
        autoplay: true,
        loop: true,
      })
      sound.on('mute', () => setMuted(sound.mute()))
      sound.on('play', () => setMuted(false))
      setSound(sound)
      return () => {
        sound.unload()
      }
    }
  }, [])

  return (
    <div ref={wrapper}>
      <div ref={canvasPin}>
        <section className="relative h-screen" ref={section}>
          <canvas
            ref={canvas}
            className="w-full h-screen object-cover touch-manipulation"
          ></canvas>
          <div className="absolute flex justify-between bottom-4 px-2 left-0 right-0">
            {audio && sound && (
              <div className={'flex justify-between items-end pr-2'}>
                <button
                  className={s.muteBtn}
                  onClick={() => {
                    sound.mute(!sound.mute())
                  }}
                >
                  <MuteIcon muted={muted} />
                  {muted && !isMobile() && 'unmute'}
                </button>
              </div>
            )}

            <div ref={componentContainer} className="w-full flex">
              {notifications &&
                notifications.map((notification, index) => (
                  <div
                    className={clsx(s.notification, {
                      [s.reverse]: notification.alignLeft,
                    })}
                    data-start={
                      isMobile()
                        ? (notification.mobileStartingFrame ??
                          notification.startingFrame)
                        : notification.startingFrame
                    }
                    data-end={
                      isMobile()
                        ? (notification.mobileEndingFrame ??
                          notification.endingFrame)
                        : notification.endingFrame
                    }
                    data-newsletter={notification.enableNewsletter}
                    key={index}
                    onClick={() => setAutoScroll(false)}
                    onMouseEnter={() => {
                      setAutoScroll(false)
                    }}
                    onMouseLeave={() => automaticScroll && setAutoScroll(true)}
                  >
                    {notification.enableNewsletter ? (
                      // <Signup
                      //   emailPlaceholderLabel={
                      //     notification.labelEmailPlaceHolder
                      //   }
                      //   clickOutside={true}
                      //   signupText={notification.text}
                      //   videoScroll={true}
                      //   professionalCheckboxLabel={
                      //     notification.labelProfessional
                      //   }
                      //   consumerCheckboxLabel={notification.labelPrivate}
                      //   acceptTerms={notification.labelAcceptTerms}
                      //   signupButtonLabel={notification.labelSignUpButton}
                      //   enableConsumerSignup={false}
                      //   enableProfessionalSignup={false}
                      //   enablePartnerSignup={false}
                      //   partnerCheckboxLabel={''}
                      //   acceptTermsReadMore={''}
                      //   enableAcceptTermsCheckbox={false}
                      //   signupCompletedMessage={''}
                      // />
                      <></>
                    ) : (
                      <div>
                        <div className="block lg:hidden">
                          <LineClampCollapse title={notification.title ?? ''}>
                            <TrustedHtml html={notification.text} />
                          </LineClampCollapse>
                        </div>
                        <div className="py-5 hidden lg:block">
                          <span className="kingcaslon text-lg">
                            {notification.title}
                          </span>
                          <TrustedHtml html={notification.text} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default ScrollVideo
